import React, { useState, useEffect, useRef } from 'react';
import AudioRecorder from './AudioRecorder';
import api from '../services/api';
import './ChatInterface.css';

const SESSION_ID_STORAGE_KEY = 'chat_session_id';

function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingFromDb, setLoadingFromDb] = useState(true);
  const messagesEndRef = useRef(null);
  const hasLoadedFromDb = useRef(false);

  // Load messages from database on mount
  useEffect(() => {
    const loadFromDatabase = async () => {
      if (hasLoadedFromDb.current) return;
      hasLoadedFromDb.current = true;

      try {
        const data = await api.getCurrentSession();
        if (data.session && data.messages.length > 0) {
          // Load session from database
          setCurrentSessionId(data.session.id);
          setMessages(data.messages);
          // Save session_id to localStorage for session continuity
          localStorage.setItem(SESSION_ID_STORAGE_KEY, data.session.id);
        } else {
          // No session in database - start fresh
          // Check if there's a session_id in localStorage from previous session
          const savedSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
          if (savedSessionId) {
            // Clear it - we don't want to continue a session without database confirmation
            localStorage.removeItem(SESSION_ID_STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Error loading session from database:', error);
        // On error, start fresh - don't use localStorage fallback for security
        localStorage.removeItem(SESSION_ID_STORAGE_KEY);
      } finally {
        setLoadingFromDb(false);
      }
    };

    loadFromDatabase();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentSessionId) {
      localStorage.setItem(SESSION_ID_STORAGE_KEY, currentSessionId);
    } else {
      localStorage.removeItem(SESSION_ID_STORAGE_KEY);
    }
  }, [currentSessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || loading) return;

    // Check for meaningful content (at least 3 alphanumeric chars)
    const meaningfulChars = trimmed.replace(/[^a-zA-Z0-9éàèùâêîôûäëïöüç]/g, '');
    if (meaningfulChars.length < 3) {
      alert('Votre message doit contenir au moins 3 caractères significatifs');
      return;
    }

    const userMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      const response = await api.sendMessage(inputText, currentSessionId);
      setCurrentSessionId(response.session_id);

      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        sources: response.sources || null
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Auto-play TTS for assistant message
      await playTTS(response.message);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Désolé, une erreur s'est produite." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleTranscription = async (audioBlob) => {
    setLoading(true);

    try {
      const text = await api.transcribeAudio(audioBlob);

      if (text && text.trim()) {
        // Auto-send the transcribed message
        setInputText(''); // Clear input first
        await sendMessageDirect(text);
      } else {
        // If transcription is empty, just put it in the input
        setInputText(text);
      }
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setLoading(false);
    }
  };

  // New function to send a message directly without reading from input
  const sendMessageDirect = async (messageText) => {
    const trimmed = messageText.trim();

    // Check for meaningful content (at least 3 alphanumeric chars)
    const meaningfulChars = trimmed.replace(/[^a-zA-Z0-9éàèùâêîôûäëïöüç]/g, '');
    if (meaningfulChars.length < 3) {
      console.warn('Message rejected: not enough meaningful characters');
      setInputText(trimmed); // Put it in input so user can see/edit
      return;
    }

    const userMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await api.sendMessage(messageText, currentSessionId);
      setCurrentSessionId(response.session_id);

      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        sources: response.sources || null
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Auto-play TTS for assistant message
      await playTTS(response.message);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Désolé, une erreur s'est produite." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const playTTS = async (text) => {
    setIsSpeaking(true);
    try {
      const audioBlob = await api.synthesizeSpeech(text);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEndSession = async () => {
    if (!currentSessionId) {
      alert('Aucune session en cours à terminer.');
      return;
    }

    if (!window.confirm(
      'Voulez-vous vraiment terminer cette session?\n\n' +
      'Un résumé sera généré et ajouté à votre profil patient.'
    )) {
      return;
    }

    setEndingSession(true);
    try {
      const result = await api.endSession(currentSessionId);
      alert('Session terminée avec succès!\n\nRésumé: ' + (result.summary || 'Généré automatiquement.'));

      // Start a new session
      setCurrentSessionId(null);
      setMessages([]);
    } catch (err) {
      alert('Erreur lors de la fin de session: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEndingSession(false);
    }
  };

  const renderSources = (sources) => {
    if (!sources || sources.length === 0) {
      return <span className="sources-none">(Réponse basée sur les connaissances générales)</span>;
    }

    // Group sources by filename and count chunks
    const sourceMap = new Map();
    for (const source of sources) {
      const key = source.filename;
      if (!sourceMap.has(key)) {
        sourceMap.set(key, new Set());
      }
      if (source.chunk_index !== undefined && source.chunk_index !== null) {
        sourceMap.get(key).add(source.chunk_index);
      }
    }

    const sourceEntries = Array.from(sourceMap.entries()).map(([filename, chunks]) => {
      const chunksArray = Array.from(chunks).sort((a, b) => a - b);
      if (chunksArray.length > 0) {
        return `${filename} (chunk${chunksArray.length > 1 ? 's' : ''} ${chunksArray.join(', ')})`;
      }
      return filename;
    });

    return <span className="sources-info">Source{sources.length > 1 ? 's' : ''}: {sourceEntries.join(', ')}</span>;
  };

  if (loadingFromDb) {
    return (
      <div className="chat-interface">
        <div className="messages-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>Chargement de la session...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <p>Bonjour cher patient</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              <div className="message-bubble">
                <span className="message-label">
                  {msg.role === 'user' ? 'Vous' : 'Assistant'}
                </span>
                <p className="message-text">{msg.content}</p>
                {msg.role === 'assistant' && (
                  <>
                    <div className="message-sources">
                      {renderSources(msg.sources)}
                    </div>
                    <button
                      className="play-audio-btn"
                      onClick={() => playTTS(msg.content)}
                      disabled={isSpeaking}
                    >
                      &#128266; {isSpeaking ? 'En lecture...' : 'Écouter'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="message assistant">
            <div className="message-bubble loading">
              <span className="typing-indicator">En train d'écrire...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        <AudioRecorder
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          onTranscription={handleTranscription}
          disabled={loading || endingSession}
        />
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Écrivez votre message ou utilisez le microphone..."
          className="message-input"
          rows={2}
          disabled={loading || endingSession}
        />
        <button
          onClick={handleSendMessage}
          disabled={!inputText.trim() || loading || endingSession}
          className="send-btn"
        >
          Envoyer
        </button>
        {currentSessionId && (
          <button
            onClick={handleEndSession}
            disabled={loading || endingSession}
            className="end-session-btn"
            title="Terminer la session et générer un résumé"
          >
            {endingSession ? 'Terminaison...' : 'Terminer'}
          </button>
        )}
      </div>
    </div>
  );
}

export default ChatInterface;
